import { useEffect, useState } from 'react';
import en from './locales/en.json';
import ru from './locales/ru.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import { saveUiLanguage } from './crypto/storage';
import { getAppLanguageFromBridge, isWebViewMode, setAppLanguageInBridge } from './utils/webviewBridge';

const supportedLanguages = ['en', 'ru', 'de', 'fr'] as const;

type SupportedLanguage = (typeof supportedLanguages)[number];

interface TranslationMap {
  [key: string]: TranslationValue;
}

type TranslationValue = string | TranslationMap;

type Translations = TranslationMap;

const translations: Record<SupportedLanguage, Translations> = {
  en,
  ru,
  de,
  fr
};

const localeMap: Record<SupportedLanguage, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  de: 'de-DE',
  fr: 'fr-FR'
};

const listeners = new Set<() => void>();

const getStoredLanguage = (): SupportedLanguage | null => {
  try {
    const stored = localStorage.getItem('language');
    if (stored && supportedLanguages.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // ignore storage errors
  }
  return null;
};

const storeLanguage = (language: SupportedLanguage) => {
  try {
    localStorage.setItem('language', language);
  } catch {
    // ignore storage errors
  }
};

const detectLanguage = (): SupportedLanguage => {
  if (isWebViewMode) {
    const bridgeLanguage = getAppLanguageFromBridge();
    if (bridgeLanguage && supportedLanguages.includes(bridgeLanguage as SupportedLanguage)) {
      storeLanguage(bridgeLanguage as SupportedLanguage);
      return bridgeLanguage as SupportedLanguage;
    }
  }

  const stored = getStoredLanguage();
  if (stored) return stored;

  const navigatorLanguage = typeof navigator !== 'undefined'
    ? (navigator.language || '').toLowerCase()
    : '';
  const match = supportedLanguages.find((lang) => navigatorLanguage.startsWith(lang));
  const detected = match || 'en';
  storeLanguage(detected);
  return detected;
};

let currentLanguage: SupportedLanguage = detectLanguage();

const persistLanguageToIndexedDb = (language: SupportedLanguage) => {
  try {
    void saveUiLanguage(language);
  } catch {
    // ignore
  }
};

// Make the current UI language visible to the Service Worker (SW can't read localStorage).
persistLanguageToIndexedDb(currentLanguage);

const getNestedValue = (obj: TranslationValue, path: string[]): string | undefined => {
  let value: TranslationValue = obj;
  for (const key of path) {
    if (!value || typeof value !== 'object' || !(key in value)) {
      return undefined;
    }
    value = value[key] as TranslationValue;
  }
  return typeof value === 'string' ? value : undefined;
};

const interpolate = (text: string, vars?: Record<string, string | number>) => {
  if (!vars) return text;
  return Object.entries(vars).reduce((acc, [key, value]) => {
    const token = `{{${key}}}`;
    return acc.split(token).join(String(value));
  }, text);
};

export const t = (key: string, vars?: Record<string, string | number>): string => {
  const path = key.split('.');
  const value =
    getNestedValue(translations[currentLanguage], path)
    ?? getNestedValue(translations.en, path)
    ?? key;
  return interpolate(value, vars);
};

export const getLocale = (language = currentLanguage): string => {
  if (supportedLanguages.includes(language as SupportedLanguage)) {
    return localeMap[language as SupportedLanguage];
  }
  return 'en-US';
};

export const getSupportedLanguages = () => [...supportedLanguages];

const setLanguage = (language: SupportedLanguage) => {
  if (!supportedLanguages.includes(language)) return;
  if (language === currentLanguage) return;
  currentLanguage = language;
  storeLanguage(language);
  persistLanguageToIndexedDb(language);
  if (isWebViewMode) {
    setAppLanguageInBridge(language);
  }
  listeners.forEach((listener) => listener());
};

export const useI18n = () => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((prev) => prev + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    t,
    language: currentLanguage,
    setLanguage
  };
};
